import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FaPrint, FaClipboardList, FaSearch } from "react-icons/fa";

// Componentes y Utils
import { API_BASE } from "../utils/api";
import { getMyAccountId } from "../utils/config";
import { hasAccountAccess } from "../utils/permissions";
import Unauthorized from "./Unauthorized";
import { useLanguage } from "../context/LanguageContext";
import { useRidePreview } from "../hooks/useRidePreview";
import RidePreviewModal from "../components/Modals/RidePreviewModal";
import CustomDatePicker from "../components/UI/CustomDatePicker";

export default function Dashboard() {
    const { t } = useLanguage();
    const history = useHistory();
    const location = useLocation();

    // ✅ User e IDs
    const [currentUser] = useState(() => JSON.parse(localStorage.getItem("user")));
    const MYACCOUNT_ID = getMyAccountId();

    // ✅ Estados de Datos
    const [lastOrders, setLastOrders] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [reportType, setReportType] = useState("");

    // ✅ Estados de Fechas
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const [from, setFrom] = useState(firstDayOfMonth);
    const [to, setTo] = useState(today);
    const [showOptions, setShowOptions] = useState(false);
    const { rideOpen, rideUrl, openRidePreview, closeRidePreview } = useRidePreview();

    useEffect(() => {
        if (currentUser) loadLastOrders();
    }, [currentUser]);

    const loadLastOrders = async (dateFrom = null, dateTo = null) => {
        try {
            setLastOrders([]); // Opcional: limpiar tabla mientras carga
            let url = `${API_BASE}/orders-ultra/api/ws/list_duedates_cust?id_customer=${currentUser.idCustomer}`;

            // Si es una búsqueda por rango, añadimos los parámetros
            if (dateFrom && dateTo) {
                url += `&date_from=${dateFrom.toISOString().split("T")[0]}`;
                url += `&date_to=${dateTo.toISOString().split("T")[0]}`;
            }

            const response = await fetch(url);
            const data = await response.json();
            const filteredData = data.filter(order => order.tipo !== 'FE');
            setLastOrders(filteredData);
        } catch (err) {
            console.error("Error cargando últimos pedidos", err);
        }
    };

    if (!currentUser) return <div className="mt-20 text-center text-gray-600">Cargando...</div>;
    if (!hasAccountAccess()) return <Unauthorized />;

    // --- Cálculos de Totales ---
    const totalVencido = lastOrders.reduce((acc, o) => acc + (Number(o.mora) > 0 ? Number(o.saldo || 0) : 0), 0);
    const totalSaldoNeto = lastOrders.reduce((acc, o) => acc + Number(o.saldo || 0), 0);
    const totalPendiente = totalSaldoNeto - totalVencido;
    const numFacturas = lastOrders.filter(o =>
        (o.tipo?.includes('FC') || o.tipo?.includes('FE') || o.tipo?.includes('ND')) && Number(o.saldo) > 0
    ).length;

    return (
        <div className="container mx-auto px-4 mt-[100px] md:mt-[180px] lg:mt-[100px]">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.menuCxP} a {currentUser.companyNameMain}</h1>
            <div className="border-b-2 border-gray-300 w-32 mb-5"></div>

            {/* HEADER - BLOQUES DE TOTALES */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">

                {/* Repetición de bloques de saldo (Total, Vencido, Pendiente, Facturas) */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Saldo Total</p>
                    <div className="flex items-baseline gap-1">
                        <span className="text-slate-400 text-sm font-medium">$</span>
                        <span className="text-slate-900 text-2xl font-extrabold tracking-tight">{totalSaldoNeto.toLocaleString('us', { minimumFractionDigits: 2 })}</span>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Vencido</p>
                    <div className="flex items-baseline gap-1">
                        <span className="text-red-500 text-sm font-medium">$</span>
                        <span className="text-red-600 text-2xl font-extrabold tracking-tight">{totalVencido.toLocaleString('us', { minimumFractionDigits: 2 })}</span>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Pendiente</p>
                    <div className="flex items-baseline gap-1">
                        <span className="text-emerald-500 text-sm font-medium">$</span>
                        <span className="text-emerald-600 text-2xl font-extrabold tracking-tight">{totalPendiente.toLocaleString('us', { minimumFractionDigits: 2 })}</span>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Facturas</p>
                    <span className="text-slate-900 text-2xl font-extrabold tracking-tight">{numFacturas}</span>
                </div>

                <div className="relative inline-block text-left w-full md:w-64">
                    {/* BOTÓN PRINCIPAL DEL DESPLEGABLE */}
                    <button
                        onClick={() => setShowOptions(!showOptions)}
                        className="flex items-center justify-between w-full px-4 py-3 bg-slate-100 border border-slate-200 rounded-xl shadow-sm hover:bg-slate-200 transition-all text-slate-700 font-bold text-xs tracking-widest"
                    >
                        <span className="flex items-center gap-2">
                            <FaClipboardList className="text-slate-500" />
                            OPCIONES DE CONSULTA
                        </span>
                        <svg
                            className={`w-4 h-4 transition-transform ${showOptions ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* MENÚ DESPLEGABLE */}
                    {showOptions && (
                        <div className="absolute right-0 z-50 mt-2 w-full origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none animate-in fade-in zoom-in duration-200">
                            <div className="p-2 space-y-1">

                                {/* OPCIÓN: HISTORIAL */}
                                <button
                                    onClick={() => { setShowDatePicker(true); setReportType("busqueda"); setShowOptions(false); }}
                                    className="flex items-center w-full gap-3 px-4 py-3 text-xs font-bold text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <div className="bg-gray-100 p-1.5 rounded-md">
                                        <FaSearch size={14} />
                                    </div>
                                    HISTORIAL POR FECHAS
                                </button>

                                {/* OPCIÓN: PENDIENTES */}
                                <button
                                    onClick={() => { setShowDatePicker(true); setReportType("pendientes"); setShowOptions(false); }}
                                    className="flex items-center w-full gap-3 px-4 py-3 text-xs font-bold text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <div className="bg-gray-100 p-1.5 rounded-md">
                                        <FaPrint size={14} />
                                    </div>
                                    FACTURAS PENDIENTES
                                </button>

                                {/* OPCIÓN: ESTADO DE CUENTA */}
                                <button
                                    onClick={() => { setShowDatePicker(true); setReportType("estado"); setShowOptions(false); }}
                                    className="flex items-center w-full gap-3 px-4 py-3 text-xs font-bold text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <div className="bg-gray-100 p-1.5 rounded-md">
                                        <FaPrint size={14} />
                                    </div>
                                    ESTADO DE CUENTA
                                </button>

                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* PANEL DE FECHAS CON COMPONENTE GLOBAL */}
            {showDatePicker && (
                /* 1. Usamos justify-end para empujar el panel a la derecha */
                <div className="flex justify-end w-full">
                    <div className="mt-4 p-5 bg-white border border-slate-200 rounded-xl shadow-lg flex flex-wrap items-end gap-6 animate-in slide-in-from-right-4 fade-in duration-300 relative z-30">

                        {/* Se muestran las fechas si es 'estado' o 'busqueda' */}
                        {(reportType === "estado" || reportType === "busqueda") && (
                            <>
                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-tighter">
                                        Desde
                                    </label>
                                    <CustomDatePicker selected={from} onChange={(d) => setFrom(d)} />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label className="text-[10px] font-bold uppercase text-slate-500 tracking-tighter">
                                        Hasta
                                    </label>
                                    <CustomDatePicker selected={to} onChange={(d) => setTo(d)} />
                                </div>
                            </>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    if (reportType === "busqueda") {
                                        loadLastOrders(from, to);
                                        setShowDatePicker(false);
                                        return;
                                    }

                                    const endpoint = reportType === "pendientes" ? "facturas_cobrar_by_customer" : "estado_cuenta_by_customer";

                                    const reportUrl = `${API_BASE}/orders-ultra/api/reports_cuentas/${endpoint}` +
                                        `?myaccount_id=${MYACCOUNT_ID}` +
                                        `&company_id=${currentUser.companyId}` +
                                        `&id_customer=${currentUser.idCustomer}` +
                                        `&date_from=${from.toISOString().split("T")[0]}` +
                                        `&date_to=${to.toISOString().split("T")[0]}` +
                                        `&user=${currentUser.userId}`;

                                    openRidePreview(reportUrl);
                                    setShowDatePicker(false);
                                }}
                                className="bg-slate-900 text-white px-6 py-2 rounded-lg text-xs font-bold hover:bg-slate-800 transition shadow-md"
                            >
                                {reportType === "pendientes" ? "GENERAR FACTURAS PENDIENTES" : reportType === "busqueda" ? "BUSCAR REGISTROS" : "GENERAR ESTADO DE CUENTA"}
                            </button>

                            <button
                                onClick={() => setShowDatePicker(false)}
                                className="bg-slate-100 text-slate-600 px-6 py-2 rounded-lg text-xs font-bold hover:bg-slate-200 transition"
                            >
                                CANCELAR
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TABLA DE REGISTROS */}
            <div className="flex justify-between items-center mt-6">
                <span className="text-sm text-gray-600">{lastOrders.length} Results</span>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2 mt-2">
                <div className="hidden md:grid grid-cols-8 px-4 py-3 bg-gray-100 border-b border-gray-300 text-gray-700 font-semibold text-sm sticky top-0 z-20">
                    <span>Fecha</span><span>Documento</span><span className="text-right">Número</span>
                    <span className="text-right">Vence el</span><span className="text-right">Mora</span>
                    <span className="text-right">Total</span><span className="text-right">Saldo</span><span></span>
                </div>
                {lastOrders.map((o) => (
                    <div key={o.orderId} onClick={() => history.push(`/order-details/${o.orderId}`, { order: o })}
                        className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm grid grid-cols-1 md:grid-cols-8 gap-2 items-center hover:bg-gray-200 transition cursor-pointer">
                        <div className="text-gray-700 text-sm">{new Date(o.orderDate).toLocaleDateString("es-ES")}</div>
                        <div className="text-gray-700 text-sm">{o.nameDocumento ?? "—"}</div>
                        <div className="text-gray-800 text-right font-semibold text-sm">
                            {o.localId && o.issueId ? `${o.localId.trim()} - ${o.issueId.trim()} - ${o.orderNumber}` : o.orderNumber}
                        </div>
                        <div className="text-right text-sm">{new Date(o.deliverBy).toLocaleDateString("es-ES")}</div>
                        <div className={`text-right text-sm ${o.mora > 0 ? 'text-red-500' : ''}`}>{o.mora !== 0 && Number(o.mora).toFixed(0)}</div>
                        <div className="text-right text-sm">${Number(o.total || 0).toFixed(2)}</div>
                        <div className="text-right text-sm">{Number(o.saldo || 0) !== 0 ? `$${Number(o.saldo).toFixed(2)}` : ""}</div>
                    </div>
                ))}
            </div>

            <RidePreviewModal open={rideOpen} url={rideUrl} onClose={closeRidePreview} />
        </div>
    );
}